import { useMemo, useState } from 'react'
import { AudioPlayerProvider, useAudioPlayer } from 'react-use-audio-player'
import { getSpeechRequest } from '@mote/api'
import { WarningCircleOutlineIcon } from '@mote/assets/icons'
import { Text } from '@mote/ui'
import {
  ErrorControl,
  StyledPlayToteButton,
  StyledSpinner
} from './PlayToteButton.styles'

/**
 * @typedef {Object} PlayToteButtonProps
 * @property {string} toteId
 */

/**
 * @param {PlayToteButtonProps} props
 * @return {JSX.Element}
 */
function _PlayToteButton(props) {
  const [playerState, setPlayerState] = useState('loading')
  const speechRequest = useMemo(
    () => getSpeechRequest(props.toteId),
    [props.toteId]
  )

  const audioPlayer = useAudioPlayer({
    src: speechRequest.url,
    format: 'mp3',
    onloaderror: () => setPlayerState('error'),
    onload: () => setPlayerState('ready'),
    onplay: () => props.onPlay?.(),
    xhr: {
      method: speechRequest.method,
      headers: Object.fromEntries(speechRequest.headers.entries())
    }
  })

  let buttonComponent = null
  let text = ''

  if (playerState === 'loading') {
    buttonComponent = <StyledSpinner />
    text = 'Loading speech...'
  }

  if (playerState === 'ready') {
    buttonComponent = (
      <StyledPlayToteButton
        isReactive={false}
        isPlaying={audioPlayer.playing}
        tooltipProps={{
          content: `${audioPlayer.playing ? 'Pause' : 'Play'}`
        }}
        onClick={() => {
          audioPlayer.playing ? audioPlayer.pause() : audioPlayer.play()
        }}
      />
    )
    text = 'Listen to the comment'
  }

  if (playerState === 'error') {
    buttonComponent = (
      <ErrorControl
        onClick={() => {
          audioPlayer.player.load()
          setPlayerState('loading')
        }}
      >
        <WarningCircleOutlineIcon color={'red'} />
      </ErrorControl>
    )
    text = 'Could not load text to speech, try again later.'
  }

  return (
    <>
      {buttonComponent}
      <Text fontWeight={500} fontSize={12} tag={'small'} color={'#6C6C84'}>
        {text}
      </Text>
    </>
  )
}

/**
 * @param {PlayToteButtonProps} props
 * @return {JSX.Element}
 */
export function PlayToteButton(props) {
  return (
    <AudioPlayerProvider>
      <_PlayToteButton toteId={props.toteId} onPlay={props.onPlay} />
    </AudioPlayerProvider>
  )
}
